import React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  styled,
} from '@mui/material';

// Styled components for enhanced visual experience
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiFormLabel-root': {
    transition: theme.transitions.create(['color']),
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiRadio-root': {
    transition: theme.transitions.create(['color', 'transform']),
    '&.Mui-checked': {
      transform: 'scale(1.05)',
    },
  },
  '& .MuiFormControlLabel-root': {
    transition: theme.transitions.create(['opacity']),
    '&:hover': {
      opacity: 0.9,
    },
  },
}));

/**
 * MagicRadioGroup - Enhanced radio group component with consistent styling
 * 
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name
 * @param {string} props.label - Field label
 * @param {Array} props.options - Array of option objects with value and label
 * @param {any} props.value - Current selected value
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.row - Whether to display options in a row
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.error - Whether the field has an error
 * @param {string} props.helperText - Helper text to display
 * @param {Function} props.renderOption - Custom render function for options
 * @param {Object} props.formControlProps - Additional props for FormControl
 * @param {Object} props.formLabelProps - Additional props for FormLabel
 * @param {Object} props.radioProps - Additional props for Radio component
 */
const MagicRadioGroup = ({
  name,
  label,
  options = [],
  value,
  onChange,
  row = false,
  required = false,
  error = false,
  helperText = '',
  renderOption,
  formControlProps = {},
  formLabelProps = {},
  radioProps = {},
  ...props
}) => {
  // Handle change event
  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.value, name);
    }
  };

  // Normalize options format
  const normalizedOptions = options.map(option => {
    if (typeof option === 'object') {
      return {
        value: option.value,
        label: option.label,
        ...option
      };
    } else {
      return {
        value: option,
        label: option
      };
    }
  });

  return (
    <StyledFormControl
      error={error}
      required={required}
      component="fieldset"
      {...formControlProps}
    >
      {label && (
        <FormLabel component="legend" {...formLabelProps}>
          {label}
        </FormLabel>
      )}
      
      <RadioGroup
        name={name}
        value={value || ''}
        onChange={handleChange}
        row={row}
        {...props}
      >
        {normalizedOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio {...radioProps} />}
            label={renderOption ? renderOption(option) : option.label}
            disabled={option.disabled}
          />
        ))}
      </RadioGroup>
      
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default MagicRadioGroup;